import React, { useContext, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';

// --- Context & Providers ---
import { FilterContext } from '@components/FilterBar/FilterProvider';

// --- Hooks ---
import { useDynamicGraphQLQuery } from '@hooks/useDynamicGraphQLQuery';

// --- Services & Constants ---
import KpiDefinitionData from 'service/KpiDefinitionData';
import { constants } from '@constants/Constants';

// --- Components ---
import CardContainer from '@components/redesign/CardContainer';
import GridContainer from '@components/redesign/GridContainer';
import KpiContainer from '@components/redesign/KpiContainer';
import FilterWrapper from '@v2/components/GDS/Filter';
import MaskOverlay from '@components/MaskOverlay';
import HorizontalProgressBarList from '@v2/components/HorizontalProgressBarList';
import AdoptionRateByBU from './AdoptionRateByBU';
import AdoptionRateByPersona from './AdoptionRateByPersona';

// --- Charts & Utils ---
import topUsersByTimeChart from '@v2/charts/tpo/topUsersByTimeChart';
import topVisitedPagesChart from 'v2/charts/tpo/topVisitedPagesChart';
import { applySuffix, formatTimeWithSeconds } from '@v2/utils/helper';

// --- Styled Components ---

const FilterContainer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const MaskableGridContainer = styled.div`
  position: relative;
`;

// --- Component ---

interface UserPersonaProps {
  title: string;
}

const UserPersona: React.FC<UserPersonaProps> = ({ title }) => {
  const { selectedProduct } = useContext(FilterContext);

  // --- State & Derived Values ---

  // Fetch market options from API
  const { data: marketsData, isLoading: marketsLoading } =
    useDynamicGraphQLQuery('getMarketsByProduct', {
      productId: selectedProduct?.productId,
      excludeMarkets: constants.markets.global,
    });

  // Derive market options from API response
  const marketFilterOptions = useMemo(() => {
    if (!marketsData || !marketsData.getMarketsByProduct.data) {
      return [];
    }

    const filteredMarkets =
      marketsData.getMarketsByProduct.data?.map((market: any) => ({
        id: market.marketId.toString(),
        displayText: market.marketName,
      })) || [];

    // Sort markets alphabetically
    filteredMarkets.sort((a: any, b: any) =>
      a.displayText.localeCompare(b.displayText)
    );
    return filteredMarkets;
  }, [marketsData]);

  // State for the user's *selected* markets
  const [selectedTPOMarket, setSelectedTPOMarket] = useState<any[]>([]);

  // State for filter validation
  const [buFilterError, setBuFilterError] = useState(false);

  // State for KPI data
  const [topUsersByTimeConfig, setTopUsersByTimeConfig] = useState<any>(null);
  const [topVisitedPagesConfig, setTopVisitedPagesConfig] = useState<any>(null);

  const maskLabel =
    'Please select at least one value from BU filter to view data';

  // --- Effects ---

  /**
   * EFFECT: Reset selected markets when the *options* change.
   * This ensures that if the user changes the product (which changes
   * the marketFilterOptions), the BU filter selection resets to "all".
   */
  useEffect(() => {
    if (marketFilterOptions.length > 0) {
      setSelectedTPOMarket(marketFilterOptions);
      // Also reset the error state when options change
      setBuFilterError(false);
    }
  }, [marketFilterOptions]);

  // --- GraphQL Queries ---

  // Prepare market IDs for the query
  const marketIds = selectedTPOMarket.map((market) => parseInt(market.id));

  const { data: topUsersByTimeData, isLoading: topUsersByTimeLoading } =
    useDynamicGraphQLQuery('getPOTopActiveUsersByTime', {
      marketIdList: marketIds.length > 0 ? marketIds : [],
      marketId: null,
    });

  const { data: topVisitedPagesData, isLoading: topVisitedPagesLoading } =
    useDynamicGraphQLQuery('getPOTopVisitedPages', {
      marketIdList: marketIds.length > 0 ? marketIds : [],
      marketId: null,
    });

  // --- Data Processing Effects ---

  useEffect(() => {
    if (topUsersByTimeData) {
      const config = topUsersByTimeChart.handler(topUsersByTimeData);
      setTopUsersByTimeConfig(config);
    }
  }, [topUsersByTimeData]);

  useEffect(() => {
    if (topVisitedPagesData) {
      const config = topVisitedPagesChart.handler(topVisitedPagesData);
      setTopVisitedPagesConfig(config);
    }
  }, [topVisitedPagesData]);

  // --- Handlers ---

  /**
   * Handles changes from the BU Filter.
   */
  const handleBUChange = (selectedValues: any) => {
    let newSelection = [];

    // Normalize `selectedValues` to always be an array
    if (Array.isArray(selectedValues)) {
      newSelection = selectedValues;
    } else if (selectedValues) {
      // Handle single selection case
      newSelection = [selectedValues];
    }

    setSelectedTPOMarket(newSelection);
    // Set error only if the user *manually* deselects everything
    setBuFilterError(newSelection.length === 0);
  };

  // --- Render Helpers ---

  // Calculate dynamic label for the filter
  const allMarketsSelected =
    marketFilterOptions.length > 0 &&
    selectedTPOMarket.length === marketFilterOptions.length;

  const filterLabel = allMarketsSelected
    ? selectedTPOMarket[0]?.displayText || 'Selected BU'
    : 'Selected BU';

  // Define header controls for the CardContainer
  const headerControls = [
    <FilterContainer key="bu-filter-container">
      <FilterWrapper
        key="bu-filter"
        options={marketFilterOptions.map((item) => ({
          id: item.id,
          label: item.displayText,
        }))}
        value={selectedTPOMarket}
        onChange={handleBUChange}
        selection="multiple"
        label={filterLabel} // Using the calculated label
        showSearch={false}
        isClearable={true}
        menuPosition="bottom-left"
        controlled={true}
        disabled={marketsLoading}
        data-testid="user-persona-bu-filter"
      />
    </FilterContainer>,
  ];

  // --- Render ---

  return (
    <CardContainer
      data-testid="user-persona-component"
      title={title}
      showInfoIcon={true}
      isAccordion={false}
      headerControls={headerControls}
      dataDefinitions={{
        sections:
          KpiDefinitionData[selectedProduct?.productId]
            ?.userBehaviorAndEngagement?.userPersona ?? [],
      }}
      isLoading={false}
    >
      <MaskableGridContainer>
        <GridContainer columnsDesktop={1}>
          <AdoptionRateByBU
            title="Adoption Rate By BU"
            selectedMarkets={selectedTPOMarket}
          />
        </GridContainer>
        {buFilterError && <MaskOverlay borderRadius="8px" label={maskLabel} />}
      </MaskableGridContainer>
      <MaskableGridContainer>
        <GridContainer columnsDesktop={1}>
          <AdoptionRateByPersona
            title="Adoption Rate By Persona"
            selectedMarkets={selectedTPOMarket}
          />
        </GridContainer>
        {buFilterError && <MaskOverlay borderRadius="8px" label={maskLabel} />}
      </MaskableGridContainer>

      <MaskableGridContainer>
        <GridContainer columnsDesktop={2}>
          <KpiContainer title="Top Users (By Active Time)">
            <HorizontalProgressBarList
              isLoading={topUsersByTimeLoading}
              hasNoData={topUsersByTimeConfig?.hasNoData}
              items={topUsersByTimeConfig?.items || []}
              wrapText={true}
              displayMode={'value'}
              valueFormatter={formatTimeWithSeconds}
            />
          </KpiContainer>
          <KpiContainer title="Top 5 Visited Pages">
            <HorizontalProgressBarList
              isLoading={topVisitedPagesLoading}
              hasNoData={topVisitedPagesConfig?.hasNoData}
              items={topVisitedPagesConfig?.items || []}
              wrapText={true}
              fixedHeight={250}
              displayMode={'value'}
              valueFormatter={applySuffix}
            />
          </KpiContainer>
        </GridContainer>
        {buFilterError && <MaskOverlay borderRadius="8px" label={maskLabel} />}
      </MaskableGridContainer>
    </CardContainer>
  );
};

export default UserPersona;
